// Grade9Ready — Maths: Section 1 Numbers
// Improved topic-bank data file for GCSE number fluency, reasoning and exam-style practice.
// Fixes included:
// - Corrected page image mapping notes
// - Standardised tier / gradeBand / difficulty values
// - Added curriculumLinks, commonMisconceptions and examTips
// - Added acceptedAnswers, units and answer normalisation support
// - Moved the book-thickness decimal multiplication question out of division
// - Added reasoning, exam-style and challenge tasks to avoid a thin workbook-only feel
// - Kept the original topic coverage while making the data easier for AI marking

export const PAGE_IMAGES = {
  // TODO: Replace this with the real Pages 2-3 scan when available.
  // The previous value incorrectly pointed to Page8-9.
  p2_3: null,
  p8_9: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/g7xi9skz_Page8-9.png",
  p10_11: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/wslto15h_Page10-11.png",
  p12_13: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/5i7asyz0_page12-13.png",
  p14_15: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/bwz8annu_Page14-15.png",
  p16_17: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/walfwia3_Page16-17.png",
  p18_19: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/c42o4rqh_Page18-19.png",
  p20_21: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/0ub53d7t_Page20-21.png",
  p22_23: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/su1y781f_Page22-23.png",
  p24_25: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/ys8fxkzl_Page24-25.png",
  p26_27: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/yw4kizzz_Page26-27.png",
  p28_29: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/9mtly2a5_Page28-29.png",
  p30_31: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/8t0hidu6_Page30-31.png",
  p32_33: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/fp2ru5id_Page32-33.png",
  p34_35: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/gyyh455i_Page34-35.png",
  p36_37: "https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/5r40jvzy_Page36-37.png",
};

export const NORMALISE_ANSWER_OPTIONS = {
  trim: true,
  lowercase: true,
  removeCommas: true,
  collapseSpaces: true,
  normaliseMinus: true,
  normaliseMultiplication: true,
  allowPercentSymbol: true,
};

const n = ({
  id,
  type = "number",
  question,
  answer,
  acceptedAnswers = [],
  unit,
  marks = 1,
  hint1,
  hint2,
  hint3,
  workingRequired = false,
  skill = "fluency",
  calculator = false,
}) => ({
  id,
  type,
  question,
  answer,
  acceptedAnswers: [...new Set([answer, ...acceptedAnswers].filter(Boolean))],
  unit,
  marks,
  hint1,
  hint2,
  hint3,
  workingRequired,
  skill,
  calculator,
});

export const numbersTopics = [
  {
    id: "1.1",
    section: "Numbers",
    title: "BODMAS",
    slug: "bodmas",
    pageRef: "Pages 2-3",
    tier: "foundation",
    gradeBand: "2-5",
    difficulty: "foundation",
    curriculumLinks: ["Order of operations", "Four operations", "Powers", "Brackets"],
    learn: {
      explanation:
        "BODMAS is the agreed order for carrying out operations in a calculation. It prevents the same calculation being interpreted in different ways.",
      keyPoints: [
        "B = Brackets first",
        "O = Orders, including powers and roots",
        "D and M = Division and Multiplication, working from left to right",
        "A and S = Addition and Subtraction, working from left to right",
        "Operations with equal priority must be completed from left to right",
      ],
      examTips: [
        "Rewrite the calculation after each stage rather than trying to do everything mentally.",
        "Division does not always come before multiplication; they have equal priority.",
        "Addition does not always come before subtraction; they have equal priority.",
      ],
      commonMisconceptions: [
        "Adding before multiplying because the addition appears first.",
        "Doing all divisions before all multiplications, rather than working left to right.",
        "Ignoring powers inside or next to brackets.",
      ],
      workedExamples: [
        {
          problem: "Calculate 3 + 4 × 2",
          steps: [
            { step: 1, instruction: "Identify the operations", working: "Addition and multiplication" },
            { step: 2, instruction: "Multiply first", working: "4 × 2 = 8" },
            { step: 3, instruction: "Then add", working: "3 + 8 = 11" },
          ],
          answer: "11",
          commonMistake: "3 + 4 = 7, then 7 × 2 = 14. This ignores multiplication before addition.",
        },
        {
          problem: "Calculate 100 − 4 × (3 + 2)²",
          steps: [
            { step: 1, instruction: "Brackets first", working: "3 + 2 = 5" },
            { step: 2, instruction: "Then powers", working: "5² = 25" },
            { step: 3, instruction: "Then multiplication", working: "4 × 25 = 100" },
            { step: 4, instruction: "Finally subtraction", working: "100 − 100 = 0" },
          ],
          answer: "0",
        },
      ],
      whyItWorks:
        "Mathematics needs a shared convention. BODMAS gives every learner and examiner the same order of operations.",
    },
    practice: {
      fluency: [
        n({ id: "1.1.f1", question: "Calculate 2 + 3 × 4", answer: "14", hint1: "Multiplication before addition", hint2: "3 × 4 = 12", hint3: "2 + 12 = 14" }),
        n({ id: "1.1.f2", question: "Calculate 10 − 2 × 3", answer: "4", hint1: "Multiplication first", hint2: "2 × 3 = 6", hint3: "10 − 6 = 4" }),
        n({ id: "1.1.f3", question: "Calculate (4 + 5) × 2", answer: "18", hint1: "Brackets first", hint2: "4 + 5 = 9", hint3: "9 × 2 = 18" }),
        n({ id: "1.1.f4", question: "Calculate 15 + 6 ÷ 2", answer: "18", hint1: "Division before addition", hint2: "6 ÷ 2 = 3", hint3: "15 + 3 = 18" }),
      ],
      reasoning: [
        n({ id: "1.1.r1", type: "text", question: "Explain why 3 + 4 × 2 is not equal to 14.", answer: "Multiplication must be done before addition, so 4 × 2 = 8 and 3 + 8 = 11.", acceptedAnswers: ["multiplication before addition", "times before add"], marks: 2, workingRequired: true, skill: "reasoning", hint1: "Which operation has priority?", hint2: "Work out 4 × 2 first.", hint3: "The correct answer is 11, not 14." }),
      ],
      examStyle: [
        n({ id: "1.1.e1", question: "Insert brackets into 6 + 4 × 3 − 2 so that the answer is 28.", answer: "(6 + 4) × 3 − 2", acceptedAnswers: ["(6+4)*3-2", "(6 + 4) x 3 - 2", "(6+4)×3−2"], marks: 2, workingRequired: true, skill: "problem-solving", hint1: "You need to make 10 first.", hint2: "Put brackets around 6 + 4.", hint3: "(6 + 4) × 3 − 2 = 28" }),
      ],
      challenge: [
        n({ id: "1.1.c1", question: "Calculate 2³ + 4 × (12 − 7)", answer: "28", hint1: "Brackets first", hint2: "2³ = 8 and 12 − 7 = 5", hint3: "8 + 4 × 5 = 28", skill: "challenge" }),
      ],
    },
  },
  {
    id: "1.2",
    section: "Numbers",
    title: "Ordering Numbers",
    slug: "ordering-numbers",
    pageRef: "Pages 3-4",
    tier: "foundation",
    gradeBand: "2-5",
    difficulty: "foundation",
    curriculumLinks: ["Place value", "Decimals", "Inequality symbols", "Ordering numbers"],
    learn: {
      explanation: "To order numbers, compare place values from left to right. With decimals, write numbers with the same number of decimal places if that helps comparison.",
      keyPoints: [
        "< means less than",
        "> means greater than",
        "Compare the largest place value first",
        "For decimals, add zeros to make equal lengths when helpful",
        "For negative numbers, the number further left on the number line is smaller",
      ],
      examTips: ["Line decimals up carefully.", "For largest-to-smallest questions, check the requested order before answering."],
      commonMisconceptions: ["Thinking 0.405 is larger than 0.45 because 405 is larger than 45.", "Forgetting that −8 is smaller than −3."],
      workedExamples: [
        {
          problem: "Put in order from smallest: 0.4, 0.45, 0.405, 0.5",
          steps: [
            { step: 1, instruction: "Write to the same number of decimal places", working: "0.400, 0.450, 0.405, 0.500" },
            { step: 2, instruction: "Compare digit by digit", working: "0.400 < 0.405 < 0.450 < 0.500" },
          ],
          answer: "0.4, 0.405, 0.45, 0.5",
        },
      ],
      whyItWorks: "Place value tells us the value of each digit, so comparing from the largest place value gives a reliable order.",
    },
    practice: {
      fluency: [
        n({ id: "1.2.f1", type: "choice", question: "Which symbol goes between 45 ___ 54?", answer: "<", acceptedAnswers: ["less than"], hint1: "Compare tens digits", hint2: "4 tens is less than 5 tens", hint3: "45 < 54" }),
        n({ id: "1.2.f2", type: "order", question: "Order from smallest: 657, 567, 675, 576", answer: "567, 576, 657, 675", acceptedAnswers: ["567 576 657 675"], hint1: "The 500s come before the 600s", hint2: "567 < 576", hint3: "657 < 675" }),
        n({ id: "1.2.f3", type: "order", question: "Order from smallest: 0.25, 0.4, 0.05, 0.15", answer: "0.05, 0.15, 0.25, 0.4", acceptedAnswers: ["0.05 0.15 0.25 0.4"], hint1: "Compare tenths", hint2: "0.05 is smallest", hint3: "Then 0.15, 0.25, 0.4" }),
      ],
      reasoning: [
        n({ id: "1.2.r1", type: "text", question: "Explain why 0.45 is greater than 0.405.", answer: "0.45 = 0.450, and 450 thousandths is greater than 405 thousandths.", acceptedAnswers: ["0.450", "450 thousandths", "405 thousandths"], marks: 2, workingRequired: true, skill: "reasoning", hint1: "Write both numbers with three decimal places.", hint2: "0.45 = 0.450", hint3: "0.450 > 0.405" }),
      ],
      examStyle: [
        n({ id: "1.2.e1", type: "order", question: "Order from largest: 12,354 / 12,543 / 12,345 / 12,435", answer: "12,543, 12,435, 12,354, 12,345", acceptedAnswers: ["12543,12435,12354,12345", "12,543 12,435 12,354 12,345"], marks: 2, hint1: "All start with 12 thousand", hint2: "Compare the hundreds digit", hint3: "5 > 4 > 3" }),
      ],
      challenge: [
        n({ id: "1.2.c1", type: "order", question: "Order from smallest: −0.4, −0.04, 0.004, −0.44, 0.04", answer: "−0.44, −0.4, −0.04, 0.004, 0.04", acceptedAnswers: ["-0.44, -0.4, -0.04, 0.004, 0.04"], marks: 2, skill: "challenge", hint1: "Negative numbers come before positive numbers.", hint2: "For negatives, the number with the larger size is smaller.", hint3: "−0.44 is less than −0.4." }),
      ],
    },
  },
  {
    id: "1.3",
    section: "Numbers",
    title: "Multiplying & Dividing by 10, 100, 1000",
    slug: "multiplying-dividing-10",
    pageRef: "Pages 5-6",
    tier: "foundation",
    gradeBand: "2-5",
    difficulty: "foundation",
    curriculumLinks: ["Place value", "Decimals", "Powers of 10"],
    learn: {
      explanation: "When multiplying or dividing by powers of 10, the digits change place value. Multiplying makes the number larger; dividing makes it smaller. A place-value grid helps avoid decimal errors.",
      keyPoints: [
        "×10 moves each digit one place to the left in a place-value grid",
        "×100 moves each digit two places to the left",
        "×1000 moves each digit three places to the left",
        "÷10 moves each digit one place to the right",
        "÷100 moves each digit two places to the right",
        "Zeros may be needed as placeholders",
      ],
      examTips: ["Avoid saying you simply add zeros; that fails with decimals.", "Use a place-value grid for numbers such as 0.045 × 1000."],
      commonMisconceptions: ["Adding zeros to decimals incorrectly, such as saying 3.4 × 10 = 3.40.", "Removing zeros from numbers where the zero is not just a placeholder."],
      workedExamples: [
        {
          problem: "Calculate 34 × 100",
          steps: [
            { step: 1, instruction: "Each digit becomes 100 times larger", working: "34 × 100" },
            { step: 2, instruction: "Move digits two places left", working: "34 → 3400" },
          ],
          answer: "3400",
        },
        {
          problem: "Calculate 8 ÷ 100",
          steps: [
            { step: 1, instruction: "Each digit becomes 100 times smaller", working: "8 ÷ 100" },
            { step: 2, instruction: "Move digits two places right", working: "8 → 0.08" },
          ],
          answer: "0.08",
        },
      ],
      whyItWorks: "The decimal system is based on powers of 10. Each place value is ten times the place to its right.",
    },
    practice: {
      fluency: [
        n({ id: "1.3.f1", question: "Calculate 45 × 10", answer: "450", hint1: "Digits move one place left", hint2: "45 becomes 450", hint3: "450" }),
        n({ id: "1.3.f2", question: "Calculate 7 × 100", answer: "700", hint1: "Digits move two places left", hint2: "7 becomes 700", hint3: "700" }),
        n({ id: "1.3.f3", question: "Calculate 230 ÷ 10", answer: "23", hint1: "Digits move one place right", hint2: "230 becomes 23", hint3: "23" }),
        n({ id: "1.3.f4", question: "Calculate 4500 ÷ 100", answer: "45", hint1: "Digits move two places right", hint2: "4500 becomes 45", hint3: "45" }),
        n({ id: "1.3.f5", question: "Calculate 0.56 × 100", answer: "56", hint1: "Digits move two places left", hint2: "0.56 becomes 56", hint3: "56" }),
        n({ id: "1.3.f6", question: "Calculate 3.5 ÷ 10", answer: "0.35", hint1: "Digits move one place right", hint2: "3.5 becomes 0.35", hint3: "0.35" }),
      ],
      reasoning: [
        n({ id: "1.3.r1", type: "text", question: "Explain why 'multiply by 10 means add a zero' is not a safe rule.", answer: "It fails for decimals. For example, 3.4 × 10 = 34, not 3.40. The digits change place value.", acceptedAnswers: ["decimals", "3.4", "place value"], marks: 2, skill: "reasoning", workingRequired: true, hint1: "Test the rule on a decimal.", hint2: "What is 3.4 × 10?", hint3: "The safer rule is that digits change place value." }),
      ],
      examStyle: [
        n({ id: "1.3.e1", question: "A number is divided by 100 and the answer is 0.347. What was the original number?", answer: "34.7", hint1: "Reverse the operation.", hint2: "Multiply 0.347 by 100.", hint3: "0.347 × 100 = 34.7", marks: 2 }),
      ],
      challenge: [
        n({ id: "1.3.c1", question: "Calculate 0.045 × 1000", answer: "45", hint1: "Move digits three places left", hint2: "0.045 becomes 45", hint3: "45", skill: "challenge" }),
      ],
    },
  },
  {
    id: "1.4",
    section: "Numbers",
    title: "Multiplying Without a Calculator",
    slug: "multiplying-without-calculator",
    pageRef: "Pages 6-7",
    tier: "foundation",
    gradeBand: "3-5",
    difficulty: "foundation",
    curriculumLinks: ["Formal written multiplication", "Grid method", "Estimation", "Problem solving"],
    learn: {
      explanation: "For larger multiplications, split numbers into place-value parts or use a formal column method. Estimate first so you can check whether your answer is sensible.",
      keyPoints: [
        "Grid method splits numbers into tens, hundreds and units",
        "Column method multiplies by each digit and then adds partial products",
        "Keep place values lined up carefully",
        "Estimate first to spot unreasonable answers",
      ],
      examTips: ["Show your method for method marks.", "In worded questions, identify the multiplication before calculating."],
      commonMisconceptions: ["Forgetting the zero placeholder when multiplying by tens.", "Adding partial products with digits misaligned."],
      workedExamples: [
        {
          problem: "Calculate 23 × 14 using the grid method",
          steps: [
            { step: 1, instruction: "Split the numbers", working: "23 = 20 + 3, 14 = 10 + 4" },
            { step: 2, instruction: "Multiply each part", working: "20×10=200, 20×4=80, 3×10=30, 3×4=12" },
            { step: 3, instruction: "Add the parts", working: "200 + 80 + 30 + 12 = 322" },
          ],
          answer: "322",
        },
      ],
      whyItWorks: "The grid method uses the distributive property: a × (b + c) = a×b + a×c.",
    },
    practice: {
      fluency: [
        n({ id: "1.4.f1", question: "Calculate 34 × 5", answer: "170", hint1: "30 × 5 = 150", hint2: "4 × 5 = 20", hint3: "150 + 20 = 170" }),
        n({ id: "1.4.f2", question: "Calculate 26 × 3", answer: "78", hint1: "20 × 3 = 60", hint2: "6 × 3 = 18", hint3: "60 + 18 = 78" }),
        n({ id: "1.4.f3", question: "Calculate 45 × 12", answer: "540", hint1: "45 × 10 = 450", hint2: "45 × 2 = 90", hint3: "450 + 90 = 540" }),
      ],
      reasoning: [
        n({ id: "1.4.r1", type: "text", question: "A student estimates 37 × 68 as about 40 × 70 = 2800. The exact answer is 2516. Explain why this is reasonable.", answer: "37 and 68 both rounded up, so the estimate should be larger than the exact answer. 2516 is close to 2800.", acceptedAnswers: ["rounded up", "larger", "close"], marks: 2, skill: "reasoning", workingRequired: true, hint1: "Did the rounded numbers increase or decrease?", hint2: "40 is bigger than 37 and 70 is bigger than 68.", hint3: "So the estimate should be higher than the exact answer." }),
      ],
      examStyle: [
        n({ id: "1.4.e1", question: "Tanisha has 37 packs of pencils. Each pack contains 68 pencils. How many pencils does she have?", answer: "2516", acceptedAnswers: ["2,516"], marks: 2, unit: "pencils", hint1: "Calculate 37 × 68", hint2: "Use grid or column multiplication", hint3: "37 × 68 = 2516" }),
      ],
      challenge: [
        n({ id: "1.4.c1", question: "Calculate 143 × 27", answer: "3861", acceptedAnswers: ["3,861"], marks: 2, skill: "challenge", hint1: "143 × 20 and 143 × 7", hint2: "2860 + 1001", hint3: "3861" }),
      ],
    },
  },
  {
    id: "1.5",
    section: "Numbers",
    title: "Dividing Without a Calculator",
    slug: "dividing-without-calculator",
    pageRef: "Pages 8-9",
    tier: "foundation",
    gradeBand: "3-5",
    difficulty: "foundation",
    curriculumLinks: ["Formal written division", "Remainders", "Inverse operations", "Problem solving"],
    learn: {
      explanation: "For division without a calculator, use short division for small divisors and long division or chunking for larger divisors. Always check by multiplying your answer by the divisor.",
      keyPoints: [
        "Short division is useful for single-digit or simple divisors",
        "For larger divisors, write useful multiples first",
        "Remainders can be left as remainders, fractions or decimals depending on the question",
        "Check using divisor × answer",
      ],
      examTips: ["For word problems, decide whether to round up or down if there is a remainder.", "Include the unit in your final answer."],
      commonMisconceptions: ["Ignoring remainders in real-life problems.", "Rounding down when the context requires an extra box, bus or container."],
      workedExamples: [
        {
          problem: "Calculate 848 ÷ 16",
          image: PAGE_IMAGES.p8_9,
          steps: [
            { step: 1, instruction: "Write useful multiples of 16", working: "16, 32, 48, 64, 80, 96" },
            { step: 2, instruction: "16 into 84 goes 5 times", working: "5 × 16 = 80, remainder 4" },
            { step: 3, instruction: "Bring down 8", working: "48 ÷ 16 = 3" },
          ],
          answer: "53",
        },
      ],
      whyItWorks: "Division is the inverse of multiplication, so each division can be checked by multiplying back.",
    },
    practice: {
      fluency: [
        n({ id: "1.5.f1", question: "Calculate 156 ÷ 12", answer: "13", hint1: "Write multiples of 12", hint2: "12 × 13 = 156", hint3: "13" }),
        n({ id: "1.5.f2", question: "Calculate 240 ÷ 15", answer: "16", hint1: "15 × 10 = 150", hint2: "15 × 16 = 240", hint3: "16" }),
        n({ id: "1.5.f3", question: "Calculate 1200 ÷ 80", answer: "15", hint1: "Simplify to 120 ÷ 8", hint2: "120 ÷ 8 = 15", hint3: "15" }),
      ],
      reasoning: [
        n({ id: "1.5.r1", type: "text", question: "A student says 936 ÷ 24 is about 40. Explain why that estimate is sensible.", answer: "24 × 40 = 960, which is close to 936, so the answer should be just under 40.", acceptedAnswers: ["24 × 40", "960", "close", "under 40"], marks: 2, skill: "reasoning", workingRequired: true, hint1: "Multiply 24 by 40.", hint2: "Compare 960 with 936.", hint3: "936 is slightly less, so the answer is slightly less than 40." }),
      ],
      examStyle: [
        n({ id: "1.5.e1", question: "A school orders 936 exercise books. They are packed equally into boxes of 24. How many boxes are needed?", answer: "39", unit: "boxes", marks: 2, hint1: "Calculate 936 ÷ 24", hint2: "24 × 40 = 960", hint3: "960 − 24 = 936, so 39 boxes" }),
        n({ id: "1.5.e2", question: "658 sweets are shared equally into bags of 28. How many full bags can be made and how many sweets are left over?", answer: "23 remainder 14", acceptedAnswers: ["23 r14", "23 remainder 14", "23 bags and 14 left"], marks: 2, hint1: "28 × 20 = 560", hint2: "658 − 560 = 98", hint3: "28 × 3 = 84, remainder 14" }),
      ],
      challenge: [
        n({ id: "1.5.c1", question: "A coach holds 52 passengers. How many coaches are needed for 689 passengers?", answer: "14", unit: "coaches", marks: 2, skill: "challenge", hint1: "689 ÷ 52", hint2: "52 × 13 = 676", hint3: "There are 13 passengers left, so 14 coaches are needed." }),
      ],
    },
  },
  {
    id: "1.6",
    section: "Numbers",
    title: "Multiplying & Dividing with Decimals",
    slug: "decimals-multiply-divide",
    pageRef: "Page 9",
    tier: "foundation",
    gradeBand: "3-5",
    difficulty: "foundation",
    curriculumLinks: ["Decimal multiplication", "Decimal division", "Place value", "Scaling"],
    learn: {
      explanation: "To multiply decimals, multiply as whole numbers first, then use place value to place the decimal point. To divide by a decimal, scale both numbers by the same power of 10 to make the divisor a whole number.",
      keyPoints: [
        "For decimal multiplication, count the total decimal places",
        "Estimate first to check the size of your answer",
        "For division by a decimal, multiply both numbers by 10, 100 or 1000",
        "Dividing both numbers by the same scale keeps the value of the division equivalent",
      ],
      examTips: ["Use an estimate to avoid decimal point errors.", "When dividing by 0.4, change 7.2 ÷ 0.4 into 72 ÷ 4."],
      commonMisconceptions: ["Placing the decimal point without estimating.", "Only multiplying one number when trying to remove a decimal divisor."],
      workedExamples: [
        {
          problem: "Calculate 3.2 × 1.8",
          steps: [
            { step: 1, instruction: "Ignore the decimals", working: "32 × 18 = 576" },
            { step: 2, instruction: "Count decimal places", working: "1 + 1 = 2 decimal places" },
            { step: 3, instruction: "Place the decimal", working: "576 → 5.76" },
          ],
          answer: "5.76",
        },
        {
          problem: "Calculate 8.48 ÷ 0.16",
          steps: [
            { step: 1, instruction: "Scale both numbers by 100", working: "8.48 ÷ 0.16 = 848 ÷ 16" },
            { step: 2, instruction: "Divide", working: "848 ÷ 16 = 53" },
          ],
          answer: "53",
        },
      ],
      whyItWorks: "Scaling both numbers in a division by the same amount creates an equivalent calculation.",
    },
    practice: {
      fluency: [
        n({ id: "1.6.f1", question: "Calculate 2.4 × 3", answer: "7.2", hint1: "24 × 3 = 72", hint2: "One decimal place", hint3: "7.2" }),
        n({ id: "1.6.f2", question: "Calculate 12.6 ÷ 3", answer: "4.2", hint1: "12 ÷ 3 = 4", hint2: "0.6 ÷ 3 = 0.2", hint3: "4.2" }),
        n({ id: "1.6.f3", question: "Calculate 4.5 × 2.3", answer: "10.35", hint1: "45 × 23 = 1035", hint2: "Two decimal places total", hint3: "10.35" }),
        n({ id: "1.6.f4", question: "Calculate 7.2 ÷ 0.4", answer: "18", hint1: "Scale by 10", hint2: "72 ÷ 4", hint3: "18" }),
      ],
      reasoning: [
        n({ id: "1.6.r1", type: "text", question: "Explain why 8.48 ÷ 0.16 is equivalent to 848 ÷ 16.", answer: "Both numbers have been multiplied by 100, so the division has the same value.", acceptedAnswers: ["multiplied by 100", "same value", "equivalent"], marks: 2, skill: "reasoning", workingRequired: true, hint1: "What happens to 8.48 if you multiply by 100?", hint2: "What happens to 0.16 if you multiply by 100?", hint3: "Both have been scaled by the same amount." }),
      ],
      examStyle: [
        n({ id: "1.6.e1", question: "A book has 301 pages. Each page is 0.06 mm thick. How thick is the book?", answer: "18.06", acceptedAnswers: ["18.06mm", "18.06 mm"], unit: "mm", marks: 2, hint1: "Calculate 301 × 0.06", hint2: "301 × 6 = 1806", hint3: "Two decimal places: 18.06 mm" }),
      ],
      challenge: [
        n({ id: "1.6.c1", question: "Calculate 0.36 ÷ 0.012", answer: "30", marks: 2, skill: "challenge", hint1: "Scale both numbers by 1000", hint2: "360 ÷ 12", hint3: "30" }),
      ],
    },
  },
  {
    id: "1.7",
    section: "Numbers",
    title: "Negative Numbers",
    slug: "negative-numbers",
    pageRef: "Pages 10-11",
    tier: "foundation",
    gradeBand: "2-5",
    difficulty: "foundation",
    curriculumLinks: ["Negative numbers", "Number lines", "Four operations", "Directed number"],
    learn: {
      explanation: "Negative numbers are less than zero. A number line is useful for addition and subtraction. For multiplication and division, use the sign rules.",
      keyPoints: [
        "Adding a positive moves right on a number line",
        "Adding a negative moves left",
        "Subtracting a negative is the same as adding the positive",
        "Same signs multiply or divide to make a positive",
        "Different signs multiply or divide to make a negative",
      ],
      examTips: ["Rewrite double negatives as addition.", "Use brackets around negative numbers in calculations."],
      commonMisconceptions: ["Thinking −8 is greater than −3 because 8 is greater than 3.", "Forgetting that subtracting a negative increases the value."],
      workedExamples: [
        {
          problem: "Calculate 3 − (−9)",
          steps: [
            { step: 1, instruction: "Subtracting a negative becomes addition", working: "3 − (−9) = 3 + 9" },
            { step: 2, instruction: "Add", working: "3 + 9 = 12" },
          ],
          answer: "12",
        },
        {
          problem: "Calculate −18 ÷ −3",
          steps: [
            { step: 1, instruction: "Divide the numbers", working: "18 ÷ 3 = 6" },
            { step: 2, instruction: "Same signs give positive", working: "− ÷ − = +" },
          ],
          answer: "6",
        },
      ],
      whyItWorks: "Directed number rules keep addition, subtraction, multiplication and division consistent across the number line.",
    },
    practice: {
      fluency: [
        n({ id: "1.7.f1", question: "Calculate −4 + 7", answer: "3", hint1: "Start at −4", hint2: "Move right 7", hint3: "3" }),
        n({ id: "1.7.f2", question: "Calculate 5 − 8", answer: "-3", acceptedAnswers: ["−3"], hint1: "Start at 5", hint2: "Move left 8", hint3: "−3" }),
        n({ id: "1.7.f3", question: "Calculate −3 × 5", answer: "-15", acceptedAnswers: ["−15"], hint1: "3 × 5 = 15", hint2: "Different signs give negative", hint3: "−15" }),
        n({ id: "1.7.f4", question: "Calculate −18 ÷ −3", answer: "6", hint1: "18 ÷ 3 = 6", hint2: "Same signs give positive", hint3: "6" }),
      ],
      reasoning: [
        n({ id: "1.7.r1", type: "text", question: "Explain why 2 − (−8) is equal to 10.", answer: "Subtracting a negative is the same as adding the positive, so 2 − (−8) = 2 + 8 = 10.", acceptedAnswers: ["subtracting a negative", "adding", "2 + 8"], marks: 2, skill: "reasoning", workingRequired: true, hint1: "What do two minus signs become?", hint2: "Rewrite the calculation.", hint3: "2 + 8 = 10" }),
      ],
      examStyle: [
        n({ id: "1.7.e1", question: "The temperature is −6°C at midnight. By midday it has risen by 11°C. What is the temperature at midday?", answer: "5", acceptedAnswers: ["5°C", "5 degrees"], unit: "°C", marks: 2, hint1: "Calculate −6 + 11", hint2: "Move right 11 from −6", hint3: "5°C" }),
      ],
      challenge: [
        n({ id: "1.7.c1", question: "Calculate 2 − (−8) + (−12)", answer: "-2", acceptedAnswers: ["−2"], skill: "challenge", hint1: "Rewrite as 2 + 8 − 12", hint2: "2 + 8 = 10", hint3: "10 − 12 = −2" }),
      ],
    },
  },
  {
    id: "1.8",
    section: "Numbers",
    title: "Special Types of Number",
    slug: "special-numbers",
    pageRef: "Pages 12-13",
    tier: "foundation",
    gradeBand: "3-5",
    difficulty: "foundation",
    curriculumLinks: ["Integers", "Rational numbers", "Irrational numbers", "Squares", "Cubes"],
    learn: {
      explanation: "Numbers can be classified in different ways. At GCSE, you should recognise integers, rational numbers, irrational numbers, square numbers and cube numbers.",
      keyPoints: [
        "Integers are whole numbers, including negative whole numbers and zero",
        "Rational numbers can be written as fractions",
        "Irrational numbers cannot be written exactly as fractions, such as π and √2",
        "Square numbers include 1, 4, 9, 16, 25, 36, 49, 64, 81 and 100",
        "Cube numbers include 1, 8, 27, 64, 125, 216, 343, 512 and 1000",
      ],
      examTips: ["1 is not prime, but it is a square number.", "√9 is rational because √9 = 3."],
      commonMisconceptions: ["Assuming every square root is irrational.", "Thinking 1 is prime."],
      workedExamples: [
        {
          problem: "Classify 7, −3, 0.5, π and √9",
          steps: [
            { step: 1, instruction: "7", working: "Integer, rational and real" },
            { step: 2, instruction: "−3", working: "Integer, rational and real" },
            { step: 3, instruction: "0.5", working: "Rational because 0.5 = 1/2" },
            { step: 4, instruction: "π", working: "Irrational" },
            { step: 5, instruction: "√9", working: "√9 = 3, so it is an integer and rational" },
          ],
          answer: "See classification",
        },
      ],
      whyItWorks: "Classifying numbers helps you decide which properties and operations apply.",
    },
    practice: {
      fluency: [
        n({ id: "1.8.f1", type: "choice", question: "Which of these is irrational: 1/2, 0.333..., √2, 0.75?", answer: "√2", acceptedAnswers: ["sqrt2", "sqrt(2)"], hint1: "Irrational means it cannot be written exactly as a fraction", hint2: "√2 is non-terminating and non-recurring", hint3: "√2" }),
        n({ id: "1.8.f2", question: "What is 7²?", answer: "49", hint1: "7 × 7", hint2: "49", hint3: "49" }),
        n({ id: "1.8.f3", question: "What is 4³?", answer: "64", hint1: "4 × 4 × 4", hint2: "16 × 4", hint3: "64" }),
      ],
      reasoning: [
        n({ id: "1.8.r1", type: "text", question: "Explain why √9 is rational but √5 is irrational.", answer: "√9 = 3, which can be written as a fraction. √5 is non-terminating and non-recurring, so it cannot be written exactly as a fraction.", acceptedAnswers: ["√9 = 3", "sqrt9 = 3", "√5", "cannot be written as a fraction"], marks: 2, skill: "reasoning", workingRequired: true, hint1: "What is √9?", hint2: "Can 3 be written as a fraction?", hint3: "√5 is not a square root of a perfect square." }),
      ],
      examStyle: [
        n({ id: "1.8.e1", question: "How many square numbers are less than 100?", answer: "9", marks: 2, hint1: "List 1² to 9²", hint2: "10² = 100, which is not less than 100", hint3: "There are 9" }),
      ],
      challenge: [
        n({ id: "1.8.c1", question: "Find a number that is both a square number and a cube number between 50 and 100.", answer: "64", marks: 2, skill: "challenge", hint1: "64 = 8²", hint2: "64 = 4³", hint3: "64" }),
      ],
    },
  },
  {
    id: "1.9",
    section: "Numbers",
    title: "Prime Numbers",
    slug: "prime-numbers",
    pageRef: "Pages 14-15",
    tier: "foundation",
    gradeBand: "3-5",
    difficulty: "foundation",
    curriculumLinks: ["Prime numbers", "Factors", "Divisibility", "Proof and reasoning"],
    learn: {
      explanation: "A prime number has exactly two factors: 1 and itself. Prime numbers are the building blocks of whole numbers.",
      keyPoints: ["1 is not prime", "2 is the only even prime number", "Check divisibility by prime numbers", "To test a number, check primes up to its square root"],
      examTips: ["Memorise primes up to at least 50.", "If a number is even and greater than 2, it is not prime."],
      commonMisconceptions: ["Calling 1 a prime number.", "Thinking all odd numbers are prime."],
      workedExamples: [
        {
          problem: "Find all prime numbers between 51 and 59",
          image: PAGE_IMAGES.p14_15,
          steps: [
            { step: 1, instruction: "Check 51", working: "51 = 3 × 17, not prime" },
            { step: 2, instruction: "Check 53", working: "Not divisible by 2, 3, 5 or 7, so prime" },
            { step: 3, instruction: "Check 59", working: "Not divisible by 2, 3, 5 or 7, so prime" },
          ],
          answer: "53 and 59",
        },
      ],
      whyItWorks: "Every whole number greater than 1 is either prime or can be made by multiplying primes.",
    },
    practice: {
      fluency: [
        n({ id: "1.9.f1", type: "choice", question: "Is 17 a prime number?", answer: "Yes", acceptedAnswers: ["yes", "y"], hint1: "Try dividing by 2, 3 and 5", hint2: "None divide exactly", hint3: "17 is prime" }),
        n({ id: "1.9.f2", type: "choice", question: "Is 21 a prime number?", answer: "No", acceptedAnswers: ["no", "n"], hint1: "Try dividing by 3", hint2: "21 ÷ 3 = 7", hint3: "21 is not prime" }),
        n({ id: "1.9.f3", question: "What is the largest prime number less than 30?", answer: "29", hint1: "Work backwards from 30", hint2: "29 is not divisible by 2, 3 or 5", hint3: "29" }),
      ],
      reasoning: [
        n({ id: "1.9.r1", type: "text", question: "Explain why 1 is not a prime number.", answer: "A prime number must have exactly two factors, 1 and itself. The number 1 has only one factor, so it is not prime.", acceptedAnswers: ["exactly two factors", "only one factor"], marks: 2, skill: "reasoning", workingRequired: true, hint1: "How many factors must a prime have?", hint2: "How many factors does 1 have?", hint3: "1 has only one factor." }),
      ],
      examStyle: [
        n({ id: "1.9.e1", question: "How many prime numbers are there between 1 and 20?", answer: "8", marks: 2, hint1: "List them", hint2: "2, 3, 5, 7, 11, 13, 17, 19", hint3: "8" }),
      ],
      challenge: [
        n({ id: "1.9.c1", question: "Find the two prime numbers that add to 40 and have a difference of 6.", answer: "17 and 23", acceptedAnswers: ["23 and 17", "17, 23", "23, 17"], marks: 2, skill: "challenge", hint1: "The numbers are close to 20.", hint2: "Try 17 and 23.", hint3: "17 + 23 = 40 and 23 − 17 = 6." }),
      ],
    },
  },
  {
    id: "1.10",
    section: "Numbers",
    title: "Multiples, Factors & Prime Factors",
    slug: "multiples-factors",
    pageRef: "Pages 15-16",
    tier: "foundation",
    gradeBand: "3-5",
    difficulty: "foundation",
    curriculumLinks: ["Multiples", "Factors", "Prime factorisation", "Product notation"],
    learn: {
      explanation: "Multiples are numbers in a times table. Factors divide exactly into a number. Prime factorisation writes a number as a product of prime numbers.",
      keyPoints: [
        "Multiples of 4 are 4, 8, 12, 16 and so on",
        "Factors of 12 are 1, 2, 3, 4, 6 and 12",
        "A factor tree keeps splitting a number until every branch is prime",
        "Use powers to collect repeated prime factors, such as 2 × 2 × 2 = 2³",
      ],
      examTips: ["List factor pairs systematically.", "Circle the prime numbers at the end of a factor tree."],
      commonMisconceptions: ["Confusing factors with multiples.", "Stopping a factor tree before every branch is prime."],
      workedExamples: [
        {
          problem: "Find the prime factorisation of 280",
          image: PAGE_IMAGES.p14_15,
          steps: [
            { step: 1, instruction: "Split 280", working: "280 = 2 × 140" },
            { step: 2, instruction: "Split 140", working: "140 = 2 × 70" },
            { step: 3, instruction: "Split 70", working: "70 = 2 × 35" },
            { step: 4, instruction: "Split 35", working: "35 = 5 × 7" },
            { step: 5, instruction: "Collect prime factors", working: "280 = 2³ × 5 × 7" },
          ],
          answer: "2³ × 5 × 7",
        },
      ],
      whyItWorks: "Every whole number greater than 1 has a unique prime factorisation.",
    },
    practice: {
      fluency: [
        n({ id: "1.10.f1", type: "choice", question: "Which of these is a factor of 28: 3, 5, 7 or 9?", answer: "7", hint1: "A factor divides exactly", hint2: "28 ÷ 7 = 4", hint3: "7" }),
        n({ id: "1.10.f2", type: "text", question: "List the first 5 multiples of 6.", answer: "6, 12, 18, 24, 30", acceptedAnswers: ["6 12 18 24 30"], hint1: "Start with 6 × 1", hint2: "Then 6 × 2, 6 × 3...", hint3: "6, 12, 18, 24, 30" }),
        n({ id: "1.10.f3", type: "text", question: "Find all the factors of 36.", answer: "1, 2, 3, 4, 6, 9, 12, 18, 36", acceptedAnswers: ["1 2 3 4 6 9 12 18 36"], marks: 2, hint1: "Use factor pairs", hint2: "1×36, 2×18, 3×12, 4×9, 6×6", hint3: "List them in order" }),
      ],
      reasoning: [
        n({ id: "1.10.r1", type: "text", question: "Explain the difference between a factor and a multiple using the number 6.", answer: "A factor divides into 6 exactly, such as 1, 2, 3 and 6. A multiple is in the 6 times table, such as 6, 12, 18 and 24.", acceptedAnswers: ["divides", "times table", "multiple"], marks: 2, skill: "reasoning", workingRequired: true, hint1: "Factors divide into a number.", hint2: "Multiples are results of multiplying.", hint3: "Use examples." }),
      ],
      examStyle: [
        n({ id: "1.10.e1", question: "Write 168 as a product of prime factors.", answer: "2³ × 3 × 7", acceptedAnswers: ["2^3 x 3 x 7", "2^3*3*7", "2 × 2 × 2 × 3 × 7"], marks: 3, workingRequired: true, hint1: "168 = 2 × 84", hint2: "84 = 2 × 42, 42 = 2 × 21", hint3: "2³ × 3 × 7" }),
      ],
      challenge: [
        n({ id: "1.10.c1", question: "A number has prime factorisation 2² × 3 × 5. What is the number?", answer: "60", marks: 2, skill: "challenge", hint1: "2² = 4", hint2: "4 × 3 × 5", hint3: "60" }),
      ],
    },
  },
  {
    id: "1.11",
    section: "Numbers",
    title: "LCM & HCF",
    slug: "lcm-hcf",
    pageRef: "Pages 16-17",
    tier: "foundation",
    gradeBand: "3-5",
    difficulty: "foundation",
    curriculumLinks: ["Lowest common multiple", "Highest common factor", "Factors", "Multiples", "Problem solving"],
    learn: {
      explanation: "The lowest common multiple is the smallest number in two or more times tables. The highest common factor is the largest number that divides exactly into two or more numbers.",
      keyPoints: [
        "LCM means lowest common multiple",
        "HCF means highest common factor",
        "Use lists for smaller numbers",
        "Use prime factorisation for larger numbers",
        "LCM is often used when events repeat; HCF is often used when sharing or grouping",
      ],
      examTips: ["LCM questions often use words like 'next together' or 'same time'.", "HCF questions often use words like 'largest possible' or 'equal groups'."],
      commonMisconceptions: ["Mixing up LCM and HCF.", "Giving a common multiple that is not the lowest common multiple."],
      workedExamples: [
        {
          problem: "Find the LCM of 9 and 15",
          image: PAGE_IMAGES.p16_17,
          steps: [
            { step: 1, instruction: "List multiples of 9", working: "9, 18, 27, 36, 45" },
            { step: 2, instruction: "List multiples of 15", working: "15, 30, 45" },
            { step: 3, instruction: "Find the first common multiple", working: "45" },
          ],
          answer: "45",
        },
      ],
      whyItWorks: "LCM and HCF compare the multiplicative structure of numbers.",
    },
    practice: {
      fluency: [
        n({ id: "1.11.f1", question: "Find the LCM of 3 and 4.", answer: "12", hint1: "Multiples of 3: 3, 6, 9, 12", hint2: "Multiples of 4: 4, 8, 12", hint3: "12" }),
        n({ id: "1.11.f2", question: "Find the HCF of 12 and 18.", answer: "6", hint1: "Factors of 12: 1, 2, 3, 4, 6, 12", hint2: "Factors of 18: 1, 2, 3, 6, 9, 18", hint3: "6" }),
        n({ id: "1.11.f3", question: "Find the LCM of 6 and 8.", answer: "24", hint1: "Multiples of 6: 6, 12, 18, 24", hint2: "Multiples of 8: 8, 16, 24", hint3: "24" }),
      ],
      reasoning: [
        n({ id: "1.11.r1", type: "text", question: "Explain why the HCF of 22, 33 and 110 is 11.", answer: "22 = 2 × 11, 33 = 3 × 11 and 110 = 2 × 5 × 11, so 11 is the largest factor common to all three numbers.", acceptedAnswers: ["2 × 11", "3 × 11", "common", "largest"], marks: 2, skill: "reasoning", workingRequired: true, hint1: "Write each number as factors.", hint2: "What factor appears in all three?", hint3: "11 is common to all three." }),
      ],
      examStyle: [
        n({ id: "1.11.e1", question: "Jacob makes cupcakes in batches of 12. He makes flapjacks in batches of 15. What is the minimum number he needs to make to have the same number of each?", answer: "60", marks: 2, hint1: "Find the LCM of 12 and 15", hint2: "Multiples of 12: 12, 24, 36, 48, 60", hint3: "Multiples of 15: 15, 30, 45, 60" }),
      ],
      challenge: [
        n({ id: "1.11.c1", question: "Two bells ring every 18 minutes and 24 minutes. They ring together at 09:00. When will they next ring together?", answer: "09:72", acceptedAnswers: ["10:12", "10.12", "10:12am", "10:12 am"], marks: 3, skill: "challenge", hint1: "Find the LCM of 18 and 24", hint2: "LCM = 72 minutes", hint3: "09:00 + 72 minutes = 10:12" }),
      ],
    },
  },
  {
    id: "1.12",
    section: "Numbers",
    title: "Fractions, Decimals & Percentages",
    slug: "fdp-conversions",
    pageRef: "Pages 19-20",
    tier: "foundation",
    gradeBand: "3-5",
    difficulty: "foundation",
    curriculumLinks: ["Fractions", "Decimals", "Percentages", "Equivalent representations"],
    learn: {
      explanation: "Fractions, decimals and percentages are different ways of representing parts of a whole. You need to convert between them accurately.",
      keyPoints: [
        "Fraction to decimal: divide the numerator by the denominator",
        "Decimal to percentage: multiply by 100",
        "Percentage to decimal: divide by 100",
        "Decimal to fraction: write over a power of 10 and simplify",
        "Key conversions include 1/2 = 0.5 = 50%, 1/4 = 0.25 = 25%, 3/4 = 0.75 = 75%",
      ],
      examTips: ["Learn common FDP equivalents by memory.", "For percentages greater than 100%, the decimal multiplier is greater than 1."],
      commonMisconceptions: ["Writing 0.4 as 4% instead of 40%.", "Thinking 120% means 0.12 instead of 1.2."],
      workedExamples: [
        {
          problem: "Convert 2/3 to a decimal and percentage",
          image: PAGE_IMAGES.p18_19,
          steps: [
            { step: 1, instruction: "Divide", working: "2 ÷ 3 = 0.666..." },
            { step: 2, instruction: "Multiply by 100", working: "0.666... × 100 = 66.666...%" },
          ],
          answer: "0.666... and 66⅔%",
        },
      ],
      whyItWorks: "Each form gives an equivalent representation of the same proportion.",
    },
    practice: {
      fluency: [
        n({ id: "1.12.f1", question: "Convert 1/2 to a decimal.", answer: "0.5", hint1: "1 ÷ 2", hint2: "0.5", hint3: "0.5" }),
        n({ id: "1.12.f2", question: "Convert 0.75 to a percentage.", answer: "75", acceptedAnswers: ["75%"], unit: "%", hint1: "Multiply by 100", hint2: "0.75 × 100", hint3: "75%" }),
        n({ id: "1.12.f3", question: "Convert 40% to a decimal.", answer: "0.4", hint1: "Divide by 100", hint2: "40 ÷ 100", hint3: "0.4" }),
      ],
      reasoning: [
        n({ id: "1.12.r1", type: "text", question: "Explain why 0.12 is equal to 3/25.", answer: "0.12 = 12/100. Dividing the numerator and denominator by 4 gives 3/25.", acceptedAnswers: ["12/100", "divide by 4", "3/25"], marks: 2, skill: "reasoning", workingRequired: true, hint1: "Write 0.12 as a fraction over 100.", hint2: "Simplify 12/100.", hint3: "12/100 = 3/25." }),
      ],
      examStyle: [
        n({ id: "1.12.e1", question: "120% of £4000 as a decimal multiplier is 1.2. What is the value?", answer: "4800", acceptedAnswers: ["£4800", "4,800", "£4,800"], unit: "£", marks: 2, hint1: "120% = 1.2", hint2: "1.2 × 4000", hint3: "4800" }),
      ],
      challenge: [
        n({ id: "1.12.c1", question: "Convert 0.345 to a fraction in its simplest form.", answer: "69/200", acceptedAnswers: ["69 over 200"], marks: 2, skill: "challenge", hint1: "0.345 = 345/1000", hint2: "Simplify by dividing by 5", hint3: "69/200" }),
      ],
    },
  },
  {
    id: "1.13",
    section: "Numbers",
    title: "Fractions Without a Calculator",
    slug: "fractions",
    pageRef: "Pages 20-23",
    tier: "foundation",
    gradeBand: "4-5",
    difficulty: "foundation-plus",
    curriculumLinks: ["Adding fractions", "Subtracting fractions", "Multiplying fractions", "Dividing fractions", "Fractions as operators"],
    learn: {
      explanation: "For addition and subtraction, use a common denominator. For multiplication, multiply numerators and denominators. For division, multiply by the reciprocal.",
      keyPoints: [
        "Add or subtract fractions only after making denominators the same",
        "Multiply numerators together and denominators together",
        "To divide by a fraction, multiply by its reciprocal",
        "Convert mixed numbers to improper fractions first",
        "Simplify final answers where possible",
      ],
      examTips: ["Do not add denominators when adding fractions.", "Look for cross-cancelling before multiplying fractions."],
      commonMisconceptions: ["Adding both numerators and denominators.", "Forgetting to flip the second fraction when dividing."],
      workedExamples: [
        {
          problem: "Calculate 5/8 + 1/3",
          image: PAGE_IMAGES.p20_21,
          steps: [
            { step: 1, instruction: "Find a common denominator", working: "LCM of 8 and 3 is 24" },
            { step: 2, instruction: "Convert", working: "5/8 = 15/24, 1/3 = 8/24" },
            { step: 3, instruction: "Add", working: "15/24 + 8/24 = 23/24" },
          ],
          answer: "23/24",
        },
      ],
      whyItWorks: "A common denominator ensures the parts being added or subtracted are the same size.",
    },
    practice: {
      fluency: [
        n({ id: "1.13.f1", question: "Calculate 1/4 + 1/3.", answer: "7/12", hint1: "Common denominator = 12", hint2: "3/12 + 4/12", hint3: "7/12" }),
        n({ id: "1.13.f2", question: "Calculate 2/3 × 3/4.", answer: "1/2", acceptedAnswers: ["6/12"], hint1: "Multiply tops", hint2: "Multiply bottoms", hint3: "6/12 = 1/2" }),
        n({ id: "1.13.f3", question: "Calculate 5/6 − 1/4.", answer: "7/12", hint1: "Common denominator = 12", hint2: "10/12 − 3/12", hint3: "7/12" }),
      ],
      reasoning: [
        n({ id: "1.13.r1", type: "text", question: "Explain why 1/4 + 1/3 is not equal to 2/7.", answer: "The denominators represent different-sized parts, so you need a common denominator. 1/4 + 1/3 = 3/12 + 4/12 = 7/12.", acceptedAnswers: ["common denominator", "different-sized parts", "7/12"], marks: 2, skill: "reasoning", workingRequired: true, hint1: "Can you add denominators directly?", hint2: "Use twelfths.", hint3: "The answer is 7/12." }),
      ],
      examStyle: [
        n({ id: "1.13.e1", question: "Find 9/20 of £360.", answer: "162", acceptedAnswers: ["£162"], unit: "£", marks: 2, hint1: "Divide by 20", hint2: "360 ÷ 20 = 18", hint3: "18 × 9 = 162" }),
      ],
      challenge: [
        n({ id: "1.13.c1", question: "Calculate 2/5 ÷ 4/3.", answer: "3/10", marks: 2, skill: "challenge", hint1: "Flip the second fraction", hint2: "2/5 × 3/4", hint3: "6/20 = 3/10" }),
      ],
    },
  },
  {
    id: "1.14",
    section: "Numbers",
    title: "Percentages",
    slug: "percentages",
    pageRef: "Pages 22-23",
    tier: "foundation",
    gradeBand: "4-5",
    difficulty: "foundation-plus",
    curriculumLinks: ["Percentage of an amount", "Percentage change", "Percentage comparison", "Multipliers"],
    learn: {
      explanation: "Percentages are parts per hundred. You should be able to find a percentage of an amount, express one amount as a percentage of another, and use multipliers for percentage change.",
      keyPoints: [
        "To find x% of y, divide by 100 then multiply by x",
        "To express x as a percentage of y, calculate x ÷ y × 100",
        "15% increase uses multiplier 1.15",
        "20% decrease uses multiplier 0.80",
        "Percentages greater than 100% represent more than the whole",
      ],
      examTips: ["Always identify the original amount in percentage change questions.", "Use multipliers for repeated percentage changes."],
      commonMisconceptions: ["Using the wrong denominator when expressing one quantity as a percentage of another.", "Thinking 125% means 0.125 rather than 1.25."],
      workedExamples: [
        {
          problem: "Find 18% of £4",
          steps: [
            { step: 1, instruction: "Find 1%", working: "£4 ÷ 100 = £0.04" },
            { step: 2, instruction: "Multiply by 18", working: "£0.04 × 18 = £0.72" },
          ],
          answer: "£0.72",
        },
      ],
      whyItWorks: "A percentage is a fraction out of 100, which makes comparisons easier.",
    },
    practice: {
      fluency: [
        n({ id: "1.14.f1", question: "Find 50% of 80.", answer: "40", hint1: "50% is half", hint2: "80 ÷ 2", hint3: "40" }),
        n({ id: "1.14.f2", question: "Find 10% of 350.", answer: "35", hint1: "Divide by 10", hint2: "350 ÷ 10", hint3: "35" }),
        n({ id: "1.14.f3", question: "Find 125% of 600 kg.", answer: "750", acceptedAnswers: ["750kg", "750 kg"], unit: "kg", hint1: "100% = 600", hint2: "25% = 150", hint3: "600 + 150 = 750" }),
      ],
      reasoning: [
        n({ id: "1.14.r1", type: "text", question: "Explain why 125% of a number is greater than the original number.", answer: "100% is the whole amount. 125% is 100% plus another 25%, so it is greater than the original.", acceptedAnswers: ["100%", "whole", "25%", "greater"], marks: 2, skill: "reasoning", workingRequired: true, hint1: "What does 100% mean?", hint2: "How much more than 100% is 125%?", hint3: "It is the whole plus one quarter." }),
      ],
      examStyle: [
        n({ id: "1.14.e1", question: "Express 28p as a percentage of 98p. Give your answer to 2 decimal places.", answer: "28.57", acceptedAnswers: ["28.57%"], unit: "%", marks: 2, calculator: true, hint1: "Calculate 28 ÷ 98", hint2: "Multiply by 100", hint3: "28.57%" }),
      ],
      challenge: [
        n({ id: "1.14.c1", question: "A price of £80 is increased by 15%. What is the new price?", answer: "92", acceptedAnswers: ["£92", "92.00", "£92.00"], unit: "£", marks: 2, skill: "challenge", hint1: "Use multiplier 1.15", hint2: "80 × 1.15", hint3: "£92" }),
      ],
    },
  },
  {
    id: "1.15",
    section: "Numbers",
    title: "Rounding Numbers",
    slug: "rounding",
    pageRef: "Pages 26-27",
    tier: "foundation",
    gradeBand: "3-5",
    difficulty: "foundation",
    curriculumLinks: ["Rounding", "Decimal places", "Significant figures", "Approximation"],
    learn: {
      explanation: "Rounding means writing a number to a chosen degree of accuracy. Look at the digit immediately after the place you are rounding to.",
      keyPoints: [
        "Decimal places count digits after the decimal point",
        "Significant figures count from the first non-zero digit",
        "If the next digit is 5 or more, round up",
        "If the next digit is less than 5, leave the rounding digit unchanged",
        "Trailing zeros can be important, for example 48.90 to 2 decimal places",
      ],
      examTips: ["Keep trailing zeros when needed to show decimal places.", "For significant figures, start counting at the first non-zero digit."],
      commonMisconceptions: ["Dropping the zero in 48.90 when the question asks for 2 decimal places.", "Counting zeros before the first non-zero digit as significant."],
      workedExamples: [
        {
          problem: "Round 39.7392739 to 2 decimal places",
          image: PAGE_IMAGES.p26_27,
          steps: [
            { step: 1, instruction: "Find the second decimal place", working: "39.73|92739" },
            { step: 2, instruction: "Look at the next digit", working: "The next digit is 9" },
            { step: 3, instruction: "Round up", working: "39.74" },
          ],
          answer: "39.74",
        },
      ],
      whyItWorks: "Rounding gives a simpler value that is close to the original number.",
    },
    practice: {
      fluency: [
        n({ id: "1.15.f1", question: "Round 21.84 to 1 decimal place.", answer: "21.8", hint1: "Look at the second decimal place", hint2: "The digit is 4", hint3: "21.8" }),
        n({ id: "1.15.f2", question: "Round 1276.7 to 2 significant figures.", answer: "1300", acceptedAnswers: ["1,300"], hint1: "First two significant figures are 1 and 2", hint2: "Next digit is 7", hint3: "1300" }),
      ],
      reasoning: [
        n({ id: "1.15.r1", type: "text", question: "Explain why 48.897 rounded to 2 decimal places is 48.90, not 48.9.", answer: "The answer must show two digits after the decimal point. 48.897 rounds to 48.90, and the zero shows the second decimal place.", acceptedAnswers: ["two decimal places", "zero", "48.90"], marks: 2, skill: "reasoning", workingRequired: true, hint1: "How many digits are needed after the decimal point?", hint2: "48.90 has two decimal places.", hint3: "The zero is needed." }),
      ],
      examStyle: [
        n({ id: "1.15.e1", question: "Round 84293 to 3 significant figures.", answer: "84300", acceptedAnswers: ["84,300"], marks: 2, hint1: "First three significant figures are 8, 4 and 2", hint2: "The next digit is 9", hint3: "84300" }),
      ],
      challenge: [
        n({ id: "1.15.c1", question: "Round 0.002309 to 2 significant figures.", answer: "0.0023", marks: 2, skill: "challenge", hint1: "Start counting at the first non-zero digit", hint2: "The first two significant figures are 2 and 3", hint3: "0.0023" }),
      ],
    },
  },
  {
    id: "1.16",
    section: "Numbers",
    title: "Estimating",
    slug: "estimating",
    pageRef: "Pages 28-29",
    tier: "foundation",
    gradeBand: "4-5",
    difficulty: "foundation-plus",
    curriculumLinks: ["Estimation", "Rounding", "Approximation", "Checking answers"],
    learn: {
      explanation: "Estimating means finding a sensible approximate answer, usually by rounding each number to 1 significant figure before calculating.",
      keyPoints: [
        "Round each number to 1 significant figure unless told otherwise",
        "Show the rounded values in your working",
        "Use the estimate to check whether an exact or calculator answer is sensible",
        "The estimate does not need to be exact",
      ],
      examTips: ["Write the rounded calculation, not just the final estimate.", "Do not over-round numbers like 0.49; one significant figure is 0.5."],
      commonMisconceptions: ["Rounding everything to the nearest whole number instead of 1 significant figure.", "Expecting an estimate to match the exact answer."],
      workedExamples: [
        {
          problem: "Estimate 63.26 × 13.12 ÷ 16.9",
          image: PAGE_IMAGES.p28_29,
          steps: [
            { step: 1, instruction: "Round to 1 significant figure", working: "63.26 ≈ 60, 13.12 ≈ 10, 16.9 ≈ 20" },
            { step: 2, instruction: "Calculate", working: "60 × 10 ÷ 20" },
            { step: 3, instruction: "Simplify", working: "600 ÷ 20 = 30" },
          ],
          answer: "≈30",
        },
      ],
      whyItWorks: "Estimation uses easier numbers to judge the approximate size of an answer.",
    },
    practice: {
      fluency: [
        n({ id: "1.16.f1", question: "Estimate 61.24 ÷ 5.92.", answer: "10", hint1: "Round to 60 ÷ 6", hint2: "60 ÷ 6", hint3: "10" }),
        n({ id: "1.16.f2", question: "Estimate 121.6 × 0.49.", answer: "60", hint1: "Round to 120 × 0.5", hint2: "Half of 120", hint3: "60" }),
      ],
      reasoning: [
        n({ id: "1.16.r1", type: "text", question: "A calculator gives 6124 ÷ 5.92 = 1034.46. Use estimation to explain whether this is sensible.", answer: "6124 rounds to 6000 and 5.92 rounds to 6. 6000 ÷ 6 = 1000, so 1034.46 is sensible.", acceptedAnswers: ["6000", "6", "1000", "sensible"], marks: 3, skill: "reasoning", workingRequired: true, hint1: "Round both numbers to 1 significant figure.", hint2: "6000 ÷ 6 = 1000.", hint3: "The calculator answer is close to the estimate." }),
      ],
      examStyle: [
        n({ id: "1.16.e1", question: "Estimate (38.4 × 28.2) ÷ (6.17 × 4.02).", answer: "50", marks: 3, hint1: "Round to (40 × 30) ÷ (6 × 4)", hint2: "1200 ÷ 24", hint3: "50" }),
      ],
      challenge: [
        n({ id: "1.16.c1", question: "Estimate 0.00491 × 8020 ÷ 0.19.", answer: "200", acceptedAnswers: ["≈200", "about 200"], marks: 3, skill: "challenge", hint1: "Round to 0.005 × 8000 ÷ 0.2", hint2: "0.005 × 8000 = 40", hint3: "40 ÷ 0.2 = 200" }),
      ],
    },
  },
  {
    id: "1.17",
    section: "Numbers",
    title: "Powers",
    slug: "powers",
    pageRef: "Pages 30-31",
    tier: "higher-foundation",
    gradeBand: "4-7",
    difficulty: "foundation-plus",
    curriculumLinks: ["Indices", "Powers", "Index laws", "Negative powers", "Zero power"],
    learn: {
      explanation: "Powers, also called indices, are shorthand for repeated multiplication. Index laws help you simplify expressions with the same base.",
      keyPoints: [
        "aⁿ means a multiplied by itself n times",
        "When multiplying the same base, add the powers",
        "When dividing the same base, subtract the powers",
        "When raising a power to a power, multiply the powers",
        "a⁰ = 1 for any non-zero value of a",
        "a⁻ⁿ = 1/aⁿ",
      ],
      examTips: ["Only use index laws directly when the base is the same.", "A negative power does not make the answer negative."],
      commonMisconceptions: ["Thinking 4⁻² = −16 instead of 1/16.", "Multiplying powers instead of adding them when multiplying the same base."],
      workedExamples: [
        {
          problem: "Simplify 2⁴ × 2⁶",
          steps: [
            { step: 1, instruction: "Same base and multiplication", working: "Add the powers" },
            { step: 2, instruction: "Add", working: "4 + 6 = 10" },
          ],
          answer: "2¹⁰",
        },
      ],
      whyItWorks: "Index laws come from counting repeated factors.",
    },
    practice: {
      fluency: [
        n({ id: "1.17.f1", question: "Calculate 2⁴.", answer: "16", hint1: "2 × 2 × 2 × 2", hint2: "4 × 4", hint3: "16" }),
        n({ id: "1.17.f2", question: "Calculate 10³.", answer: "1000", acceptedAnswers: ["1,000"], hint1: "10 × 10 × 10", hint2: "100 × 10", hint3: "1000" }),
        n({ id: "1.17.f3", question: "Simplify n⁵ × n². Write your answer using ^ notation.", answer: "n^7", acceptedAnswers: ["n⁷"], hint1: "Same base, so add powers", hint2: "5 + 2 = 7", hint3: "n^7" }),
      ],
      reasoning: [
        n({ id: "1.17.r1", type: "text", question: "Explain why a³ × a² = a⁵, not a⁶.", answer: "a³ means three factors of a and a² means two factors of a. Altogether there are five factors of a, so the answer is a⁵.", acceptedAnswers: ["three factors", "two factors", "five factors", "a^5"], marks: 2, skill: "reasoning", workingRequired: true, hint1: "Write out the repeated factors.", hint2: "a × a × a × a × a", hint3: "There are five factors." }),
      ],
      examStyle: [
        n({ id: "1.17.e1", question: "Simplify m¹³ ÷ m⁵. Write your answer using ^ notation.", answer: "m^8", acceptedAnswers: ["m⁸"], marks: 1, hint1: "Same base, so subtract powers", hint2: "13 − 5 = 8", hint3: "m^8" }),
      ],
      challenge: [
        n({ id: "1.17.c1", question: "Calculate 4⁻².", answer: "1/16", acceptedAnswers: ["0.0625"], marks: 2, skill: "challenge", hint1: "Negative power means reciprocal", hint2: "4⁻² = 1/4²", hint3: "1/16" }),
      ],
    },
  },
  {
    id: "1.18",
    section: "Numbers",
    title: "Roots & Standard Form",
    slug: "roots-standard-form",
    pageRef: "Pages 32-33",
    tier: "higher-foundation",
    gradeBand: "4-7",
    difficulty: "foundation-plus",
    curriculumLinks: ["Square roots", "Cube roots", "Standard form", "Powers of 10"],
    learn: {
      explanation: "Roots are the inverse of powers. Standard form writes very large or very small numbers as A × 10ⁿ, where 1 ≤ A < 10 and n is an integer.",
      keyPoints: [
        "√81 = 9 because 9² = 81",
        "∛27 = 3 because 3³ = 27",
        "Standard form is A × 10ⁿ where 1 ≤ A < 10",
        "Large numbers use positive powers of 10",
        "Small decimals use negative powers of 10",
      ],
      examTips: ["Check that the first number in standard form is at least 1 and less than 10.", "Use negative powers for numbers smaller than 1."],
      commonMisconceptions: ["Writing 45 × 10³ instead of 4.5 × 10⁴.", "Using a positive power for small decimals."],
      workedExamples: [
        {
          problem: "Write 259,000 in standard form",
          steps: [
            { step: 1, instruction: "Choose A", working: "2.59" },
            { step: 2, instruction: "Count place movements", working: "The decimal point has moved 5 places" },
            { step: 3, instruction: "Write standard form", working: "2.59 × 10⁵" },
          ],
          answer: "2.59 × 10⁵",
        },
        {
          problem: "Write 0.00335 in standard form",
          steps: [
            { step: 1, instruction: "Choose A", working: "3.35" },
            { step: 2, instruction: "Count place movements", working: "3 places" },
            { step: 3, instruction: "Use a negative power", working: "3.35 × 10⁻³" },
          ],
          answer: "3.35 × 10⁻³",
        },
      ],
      whyItWorks: "Standard form uses powers of 10 to express scale efficiently.",
    },
    practice: {
      fluency: [
        n({ id: "1.18.f1", question: "Calculate √49.", answer: "7", hint1: "What number squared is 49?", hint2: "7 × 7 = 49", hint3: "7" }),
        n({ id: "1.18.f2", question: "Calculate ∛125.", answer: "5", hint1: "What number cubed is 125?", hint2: "5 × 5 × 5 = 125", hint3: "5" }),
        n({ id: "1.18.f3", question: "Write 45,000 in standard form.", answer: "4.5 × 10^4", acceptedAnswers: ["4.5 x 10^4", "4.5*10^4", "4.5 × 10⁴"], hint1: "A = 4.5", hint2: "The decimal moves 4 places", hint3: "4.5 × 10⁴" }),
      ],
      reasoning: [
        n({ id: "1.18.r1", type: "text", question: "Explain why 45 × 10³ is not standard form, even though it equals 45,000.", answer: "In standard form, the first number must be at least 1 and less than 10. 45 is greater than 10, so it should be written as 4.5 × 10⁴.", acceptedAnswers: ["less than 10", "45 is greater than 10", "4.5 × 10^4"], marks: 2, skill: "reasoning", workingRequired: true, hint1: "What must A be in A × 10ⁿ?", hint2: "Is 45 less than 10?", hint3: "Use 4.5 instead." }),
      ],
      examStyle: [
        n({ id: "1.18.e1", question: "An online shop has 1.5 × 10⁶ customers. If each spends an average of £50, what is the total spent?", answer: "75000000", acceptedAnswers: ["75,000,000", "£75,000,000", "£75000000", "7.5 × 10^7"], unit: "£", marks: 2, hint1: "1.5 × 10⁶ = 1,500,000", hint2: "1,500,000 × 50", hint3: "75,000,000" }),
      ],
      challenge: [
        n({ id: "1.18.c1", question: "Write 0.0062 in standard form.", answer: "6.2 × 10^-3", acceptedAnswers: ["6.2 x 10^-3", "6.2*10^-3", "6.2 × 10⁻³"], marks: 2, skill: "challenge", hint1: "A = 6.2", hint2: "The decimal has moved 3 places", hint3: "6.2 × 10⁻³" }),
      ],
    },
  },
];

export default numbersTopics;
